import {AfterViewInit, Component, inject, OnInit, ViewChild} from '@angular/core';
import {AlertOpeService} from "../../../services/ope/alertOpe.service";
import {Alarm, MetricInstanceAlert, PartitionAlarm} from "../../../models/diagnostic/PartitionAlarm";
import {ChangePartitionEvent} from "../../../events/ChangePartitionEvent";
import {PartitionSelectionService} from "../../../services/partition/partition-selection.service";
import {BaseAlarmComponent} from "../common/baseAlarm.component";
import {GetMetricInstanceAlertStatusCB} from "../../../ope/alert/GetMetricInstanceAlertStatusCB";
import {PutMetricInstanceAlertCB} from "../../../ope/alert/PutMetricInstanceAlertCB";
import {GetMetricInstanceAlertEventsCB} from "../../../ope/alert/GetMetricInstanceAlertEventsCB";
import {AlarmEvent} from "../../../models/diagnostic/AlarmEvent";
import {MetricAlarmStatusEnum} from "../../../models/notification/MetricAlarmStatusLite";
import {MetricAlarmNotificationService} from "../../../services/notification/interfaces/MetricAlarmNotificationService";
import {NotificationService} from "../../../services/notification/notification.service";
import {MetricAlarmStatusChangedLiteNtf} from "../../../events/notification/MetricAlarmStatusChangedLiteNtf";
import {GetMetricInstanceAlertStatusDetailCB} from "../../../ope/alert/GetMetricInstanceAlertStatusDetailCB";
import {GetWeekCalendarCB} from "../../../ope/alert/GetWeekCalendarCB";
import {DatePipe} from "@angular/common";
import {DelMetricInstanceAlertCB} from "../../../ope/alert/DelMetricInstanceAlertCB";
import {DuplicateMetricInstanceAlertCB} from "../../../ope/alert/DuplicateMetricInstanceAlertCB";
import {EditModeEvent} from "../../../events/EditModeEvent";
import {TypeOfDateSettingsEnumAlarms} from "../../../enums/dashboard/TypeOfDateSettingsEnumAlarms";
import {DxDataGridComponent} from "devextreme-angular";
import {MetricAlarmStatusChangedDetailNtf} from "../../../events/notification/MetricAlarmStatusChangedDetailNtf";
import {
  MetricTelemetryNotificationService
} from "../../../services/notification/interfaces/MetricTelemetryNotificationService";
import {DateIntervalDTO} from "../../../ope/diagnostic/GetDiagnosticEventsCB";
import {UpdateMetricValueNtf} from "../../../events/notification/UpdateMetricValueNtf";
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.css', '../common/baseAlarm.component.css']
})
export class AlertComponent extends BaseAlarmComponent implements OnInit, AfterViewInit {

  @ViewChild('dataGrid') eventsTable?: DxDataGridComponent;

  protected partitionAlarms: PartitionAlarm[];

  private static readonly IN_ALARM_STATUSES: MetricAlarmStatusEnum[] = [
    MetricAlarmStatusEnum.ALARM_ON,
    MetricAlarmStatusEnum.ALARM_ON_RANGE_UP,
    MetricAlarmStatusEnum.ALARM_ON_RANGE_DOWN,
    MetricAlarmStatusEnum.ALARM_ON_STABLE,
    MetricAlarmStatusEnum.ALARM_ON_CHANGED
  ];

  protected metricFilters: MetricAlarmStatusEnum[];

  protected searchInput: string = "";

  protected alertOpeService: AlertOpeService = inject(AlertOpeService);

  protected partitionSelection: PartitionSelectionService = inject(PartitionSelectionService);

  protected datePipe: DatePipe = inject(DatePipe);

  protected metricAlarmNotificationService: MetricAlarmNotificationService = inject(NotificationService);

  protected metricTelemetryNotificationService: MetricTelemetryNotificationService = inject(NotificationService);

  protected selectedItem?: MetricInstanceAlert;

  protected partitionId: number | null = null;

  protected loadingData: boolean = false;

  protected subscribedLiteNotificationIds: number[] = [];

  protected subscribedMetricNotificationIds: number[] = [];

  protected subscribedDetailNotificationId?: number;

  protected formItem?: MetricInstanceAlert;

  private pendingUpdates = new Map<number, UpdateMetricValueNtf['value']>();
  private flushUpdates$ = new Subject<void>();

  protected selectedAlertEvents: AlarmEvent[] = [];

  protected tooltipTarget?: HTMLDivElement;

  protected showTooltip: boolean = false;

  protected tooltipContent?: string;

  protected formValid: boolean = true;

  protected formEdited: boolean = false;

  protected selectedTab: number = 0;

  /**
   * Alert being duplicated, opens the duplicate dialog when set
   */
  protected duplicatingAlert?: MetricInstanceAlert;

  /**
   * Name for the new alert, edited in the duplicate dialog
   */
  protected duplicateAlertName: string = '';

  protected readonly MetricAlarmStatusEnum = MetricAlarmStatusEnum;

  constructor() {
    super();
    this.partitionAlarms = [];
    this.metricFilters = [MetricAlarmStatusEnum.ALARM_OFF, ...AlertComponent.IN_ALARM_STATUSES];
  }

  private _editCreateMode: boolean = false;

  get editCreateMode(): boolean {
    return this._editCreateMode;
  }

  set editCreateMode(editCreateMode: boolean) {
    this._editCreateMode = editCreateMode;
    this.eventBus.emitEvent(new EditModeEvent(editCreateMode));
  }

  ngOnInit() {

    super.ngOnInit();
    this.subscriptions = [
      this.metricAlarmNotificationService.onMetricAlarmStatusChangedLite((event: MetricAlarmStatusChangedLiteNtf) => {
        this.partitionAlarms.forEach(partition => {
          if (!!partition.metricInstanceAlarms) {
            let metricAlarm = partition.metricInstanceAlarms.find((item) => item.id === event.value.alertId);
            if (!!metricAlarm && !!metricAlarm.metricInstance) {
              metricAlarm.metricInstance.lastValue = event.value.telemetryValue !== undefined && event.value.telemetryValue !== null ? event.value.telemetryValue : "---";
              metricAlarm.alarmStatus = event.value.alertStatus;
              if (!!this.selectedItem && !!this.selectedItem.metricInstance) {
                this.selectedItem.metricInstance.lastValue = event.value.telemetryValue !== undefined && event.value.telemetryValue !== null ? event.value.telemetryValue : "---";
              }
            }
          }
        });
      }),
      this.metricAlarmNotificationService.onMetricAlarmStatusChangedDetail((event: MetricAlarmStatusChangedDetailNtf) => {
        if (!!this.selectedItem && this.selectedItem.id === event.value.alertId && !!this.selectedItem.metricInstance) {
          if (!this.selectedItem.alarmDetail) {
            this.selectedItem.alarmDetail = new Alarm();
          }
          this.selectedItem.alarmDetail.endDate = event.value.endTs;
          this.selectedItem.alarmDetail.beginDate = event.value.beginTs;
          this.selectedItem.alarmDetail.isInAlarmStatus = event.value.isInAlarm;
          this.selectedItem.metricInstance.lastValue = event.value.telemetryValue;
          this.selectedItem.alarmStatus = event.value.alertStatus;
          let tsToCompare;
          if (event.value.isInAlarm) {
            tsToCompare = event.value.beginTs;
          } else {
            tsToCompare = event.value.endTs;
          }
          if (isNaN(new Date(tsToCompare).getTime())) {
            tsToCompare = new Date().getTime();
          }
          if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS || this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.REALTIME || (this.startDate >= new Date(tsToCompare) && this.endDate <= new Date(tsToCompare))) {
            let newEvent = new AlarmEvent();
            newEvent.alarmStatusValue = event.value.alertStatus;
            newEvent.alarmStatus = this.translate.instant("ALERT." + MetricAlarmStatusEnum[event.value.alertStatus]);
            newEvent.ts = new Date(tsToCompare).getTime();
            newEvent.message = event.value.message;
            this.selectedAlertEvents = [newEvent].concat(this.selectedAlertEvents);
            this.cdr.detectChanges();
            this.sortEvents();
            if (!!this.eventsTable) {
              this.eventsTable.instance.refresh()
            }
          }
        }
      }),
      this.metricTelemetryNotificationService.onUpdateMetricValue((event: UpdateMetricValueNtf) => {
        this.pendingUpdates.set(event.value.metricId, event.value);
        this.flushUpdates$.next();
      }),
      this.flushUpdates$.pipe(debounceTime(300)).subscribe(() => {
        this.pendingUpdates.forEach((update) => {
          this.partitionAlarms.forEach((partition) => {
            partition.metricInstanceAlarms?.forEach((metricInstanceAlert) => {
              if (!!metricInstanceAlert.metricInstance && metricInstanceAlert.metricInstance.id === update.metricId) {
                metricInstanceAlert.metricInstance.lastValue = update.value;
                metricInstanceAlert.metricInstance.lastValueTs = update.timestamp;
              }
            });
          });
        });
        this.pendingUpdates.clear();
      })
    ]
  }

  ngAfterViewInit() {
    // Subscribed before the first load resolves, so leaving the page mid-load still unsubscribes it
    this.subscriptions.push(
      this.eventBus.onEvent(ChangePartitionEvent.NAME, () => {
        this.loadAlertMetrics();
      })
    );
    this.loadAlertMetrics();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.flushUpdates$.complete();
    this.metricAlarmNotificationService.removeMetricIdsToAlarmLiteNotifications(this.subscribedLiteNotificationIds);
    this.metricTelemetryNotificationService.removeMetricIdsToNotifications(this.subscribedMetricNotificationIds);
    if (this.subscribedDetailNotificationId !== undefined)
      this.metricAlarmNotificationService.removeMetricIdsToAlarmDetailNotifications(this.subscribedDetailNotificationId)
  }

  filterMetrics(filter: MetricAlarmStatusEnum) {
    const group = filter === MetricAlarmStatusEnum.ALARM_ON ? AlertComponent.IN_ALARM_STATUSES : [filter];
    if (this.metricFilters.includes(filter)) {
      this.metricFilters = this.metricFilters.filter(s => !group.includes(s));
    } else {
      this.metricFilters.push(...group);
    }
  }

  loadAlertMetrics() {
    return new Promise<void>((resolve, reject) => {
      if (!!this.partitionSelection.selectedPartitionId || this.partitionSelection.selectedPartitionId === 0) {
        let cb = new GetMetricInstanceAlertStatusCB();
        cb.i.partitionId = this.partitionSelection.selectedPartitionId;
        cb.i.metricInstanceAlarmStatusSet = this.metricFilters;
        this.loadingData = true;
        this.alertOpeService.getMetricInstanceAlertStatus(cb).then(
          () => {
            this.partitionAlarms = cb.o.partitions;
            this.metricAlarmNotificationService.removeMetricIdsToAlarmLiteNotifications(this.subscribedLiteNotificationIds);
            this.metricTelemetryNotificationService.removeMetricIdsToNotifications(this.subscribedMetricNotificationIds);
            this.subscribedLiteNotificationIds = [];
            this.subscribedMetricNotificationIds = [];
            this.partitionAlarms.forEach(
              (partition: PartitionAlarm) => {
                if (!!partition.metricInstanceAlarms) {
                  this.subscribedLiteNotificationIds = this.subscribedLiteNotificationIds.concat(partition.metricInstanceAlarms.map(x => x.metricInstance!.id));
                }
              });
            this.subscribedLiteNotificationIds.flat();
            this.subscribedMetricNotificationIds = this.subscribedLiteNotificationIds;
            this.metricAlarmNotificationService.addMetricIdsToAlarmLiteNotifications(this.subscribedLiteNotificationIds);
            this.metricTelemetryNotificationService.addMetricIdsToNotifications(this.subscribedMetricNotificationIds);
            resolve();
            this.loadingData = false;
          }).catch(
          () => {
            this.loadingData = false;
            reject();
          })
      }
    })
  }

  openMetricAlarmDetail(partition: PartitionAlarm, metricAlarm: MetricInstanceAlert) {
    if (this.selectedItem?.id === metricAlarm.id && this.isDrawerOpen) {
      this.isDrawerOpen = false;
    } else {
      this.selectedItem = metricAlarm;
      this.partitionId = partition.id;
      this.selectedAlertEvents = [];
      this.initializeClickedItemAlerts();
      this.editCreateMode = false;
      this.isDrawerOpen = true;
      this.selectedTab = 0;
    }
  }

  openCreateForm(partitionId: number) {
    this.editCreateMode = false;
    new Promise<void>((resolve) => {
      this.selectedTab = 1;
      this.selectedItem = new MetricInstanceAlert();
      this.formItem = new MetricInstanceAlert();
      this.isDrawerOpen = true;
      this.partitionId = partitionId;
      this.editCreateMode = true;
      resolve();
    })
  }

  openEditForm() {
    this.editCreateMode = true;
    this.selectedTab = 1;
    this.formItem = this.appService.deepCopy(this.selectedItem);
  }

  reloadAlertEvents() {
    this.initializeClickedItemAlerts()
  }

  saveAlert() {
    if (!!this.formItem && !!this.formItem.metricInstance) {
      let cb = new PutMetricInstanceAlertCB();

      cb.i.id = this.formItem.id;
      cb.i.metricInstanceId = this.formItem.metricInstance.id;
      cb.i.name = this.formItem.name;
      cb.i.enabled = this.formItem.enabled;
      cb.i.beginAlarmNotificationOn = this.formItem.beginAlarmNotificationOn;
      cb.i.endAlarmNotificationOn = this.formItem.endAlarmNotificationOn;
      // cb.i.alarmStatus = this.formItem.alarmStatus;
      cb.i.alarmType = this.formItem.alarmType;
      cb.i.additionalInfo = this.formItem.additionalInfo;
      // Calendar in the same call: the BE checks the alarm right after saving, and must already use the new schedule
      if (this.isCalendarChanged()) {
        cb.i.updateCalendar = true;
        cb.i.calendar = this.formItem.calendar ?? null;
        cb.i.calendarForcedRecovery = !!this.formItem.calendarForcedRecovery;
        cb.i.alarmCooldownSec = this.formItem.alarmCooldownSec ?? null;
      }
      this.loadingData = true;
      this.alertOpeService.putMetricInstanceAlert(cb).then(
        () => {
          if (!!this.formItem && cb.o.id !== undefined && !!this.formItem.metricInstance) {
            this.subscribedLiteNotificationIds.push(this.formItem.metricInstance.id);
            this.subscribedMetricNotificationIds.push(this.formItem.metricInstance.id);
            this.metricAlarmNotificationService.addMetricIdsToAlarmLiteNotifications([this.formItem.metricInstance.id]);
            this.metricTelemetryNotificationService.addMetricIdsToNotifications([this.formItem.metricInstance.id]);
            if (!!this.subscribedDetailNotificationId)
              this.metricAlarmNotificationService.removeMetricIdsToAlarmDetailNotifications(this.subscribedDetailNotificationId);
            this.subscribedDetailNotificationId = this.formItem.metricInstance.id;
            this.metricAlarmNotificationService.addMetricIdsToAlarmDetailNotifications(this.subscribedDetailNotificationId);
          }
          this.selectedItem = this.appService.deepCopy(this.formItem);
          this.toastService.openSuccessToastMessage(this.translate.instant("ALERT.SAVE_OK"));
          if (this.selectedItem) {
            this.selectedItem.id = cb.o.id;
            this.loadAlertMetrics().then(
              () => {
                let originPartition = this.partitionAlarms.find(x => x.id === this.partitionId);
                if (!!originPartition && !!originPartition.metricInstanceAlarms) {
                  let originItem = originPartition.metricInstanceAlarms.find(x => x.id === cb.o.id);
                  if (!!originItem) {
                    this.selectedItem = originItem;
                  }
                }
                this.initializeClickedItemAlerts();
                this.selectedTab = 0;
              }
            )
          }
        }).catch(
        () => {
          this.loadingData = false;
        });
    }
  }

  private loadCalendar(item: MetricInstanceAlert): Promise<void> {
    const cb = new GetWeekCalendarCB();
    cb.i.alertId = item.id;
    return this.alertOpeService.getWeekCalendar(cb).then(
      () => {
        item.calendar = cb.o.calendar ?? null;
        item.calendarForcedRecovery = cb.o.calendarForcedRecovery;
        item.alarmCooldownSec = cb.o.alarmCooldownSec ?? null;
      },
      () => undefined
    );
  }

  // false = calendar unchanged: not sent, the BE leaves it as it is
  private isCalendarChanged(): boolean {
    const calendarConfig = (item?: MetricInstanceAlert | null) =>
      JSON.stringify([item?.calendar ?? null, !!item?.calendarForcedRecovery, item?.alarmCooldownSec ?? null]);
    return calendarConfig(this.formItem) !== calendarConfig(this.selectedItem);
  }

  initializeClickedItemAlerts() {
    if (!!this.selectedItem && this.selectedItem.id !== undefined && !!(this.partitionId || this.partitionId === 0)) {
      let cb = new GetMetricInstanceAlertEventsCB();
      if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
        cb.i.lastEvents = true;
        cb.i.lastEventsNumber = this.lastEventsNumber;
      } else if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.FIXED_DATE) {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date(this.endDate).getTime();
        cb.i.dateInterval.fromTs = new Date(this.startDate).getTime();
      } else {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date().getTime();
        cb.i.dateInterval.fromTs = this.getTsFromRealTime().getTime()
      }
      cb.i.metricInstanceAlertId = this.selectedItem.id;
      cb.i.partitionId = this.partitionId;
      this.loadingData = true;
      this.alertOpeService.getMetricInstanceAlertEvents(cb).then(
        () => {
          const apiTs = new Set(cb.o.events.map((e: AlarmEvent) => +new Date(e.ts)));
          const pending = this.selectedAlertEvents.filter(e => !apiTs.has(+new Date(e.ts)));
          this.selectedAlertEvents = [...cb.o.events, ...pending];
          this.sortEvents();
          this.selectedAlertEvents.forEach((alert) => {
            alert.alarmStatus = this.translate.instant("ALERT." + MetricAlarmStatusEnum[alert.alarmStatusValue]);
          });
          let detailCB = new GetMetricInstanceAlertStatusDetailCB();
          if (!!this.selectedItem && !!(this.partitionId || this.partitionId === 0)) {
            detailCB.i.partitionId = this.partitionId!;
            detailCB.i.id = this.selectedItem!.id;
            this.alertOpeService.getMetricInstanceAlertStatusDetail(detailCB).then(
              () => {
                this.selectedItem = detailCB.o;
                if (!this.selectedItem.alarmDetail)
                  this.selectedItem.alarmDetail = new Alarm();
                this.loadCalendar(this.selectedItem).then(() => {
                  this.formItem = this.appService.deepCopy(this.selectedItem);
                  this.loadingData = false;
                  this.cdr.detectChanges();
                });
              }
            ).catch(
              () => {
                this.loadingData = false;
                this.isDrawerOpen = false;
              });
          }
        }).catch(
        () => {
          this.loadingData = false;
          this.isDrawerOpen = false;
        });
    }
  }

  sortEvents() {
    this.selectedAlertEvents = this.selectedAlertEvents.sort((a, b) => {
      return new Date(b.ts).getTime() - new Date(a.ts).getTime()
    });
    this.cdr.detectChanges();
  }

  deleteAlert() {
    if (!!this.selectedItem && !!this.selectedItem.metricInstance)
      this.showDeleteDialog(this.selectedItem?.name).show().done(
        (result: boolean) => {
          if (result) {
            let cb = new DelMetricInstanceAlertCB();
            cb.i.id = this.selectedItem!.id;
            cb.i.partitionId = this.selectedItem!.metricInstance!.partitionId;
            this.alertOpeService.delMetricInstanceAlert(cb).then(() => {
              this.loadAlertMetrics().then(() => this.isDrawerOpen = false);
            })
          }
        }
      )
  }

  /**
   * Open the duplicate alert dialog, prefilling the new name
   */
  openDuplicateAlert() {
    if (!this.selectedItem) return;
    this.duplicatingAlert = this.selectedItem;
    const suffix = ' - ' + this.translate.instant('ALERT.DUPLICATE_NAME_SUFFIX');
    this.duplicateAlertName = this.selectedItem.name.slice(0, Math.max(this.gmService.STR_DIM_BIG - suffix.length, 0)) + suffix;
  }

  /**
   * Duplicates the alert, keeping the source's detail open
   */
  duplicateAlert() {
    const cb = new DuplicateMetricInstanceAlertCB();
    cb.i.alertId = this.duplicatingAlert!.id;
    cb.i.name = this.duplicateAlertName;

    this.alertOpeService.duplicateMetricInstanceAlert(cb).then(() => {
      this.toastService.openSuccessToastMessage(this.translate.instant("ALERT.DUPLICATED"));
      this.duplicatingAlert = undefined;
      this.loadAlertMetrics();
    });
  }

  undoAlertEdit() {
    if (this.formEdited || (!!this.formItem && this.formItem.id === undefined)) {
      this.showDiscardEditsDialog().show().done(
        (result: boolean) => {
          this.editCreateMode = !result;
          if (!!this.selectedItem) {
            this.loadAlertMetrics().then(
              () => {
                let originPartition = this.partitionAlarms.find(x => x.id === this.partitionId);
                if (!!originPartition && !!originPartition.metricInstanceAlarms && !!this.selectedItem) {
                  let originItem = originPartition.metricInstanceAlarms.find(x => x.id === this.selectedItem!.id);
                  if (!!originItem) {
                    this.selectedItem = originItem;
                  }
                }
                this.initializeClickedItemAlerts();
                this.selectedTab = 0;
              }
            )
          }
          if (!!this.formItem && this.formItem.id === undefined && result) {
            this.isDrawerOpen = false;
          }
        }
      )
    } else {
      this.editCreateMode = false;
    }
  }

  closeDrawer() {
    if ((this.formEdited || (!!this.formItem && this.formItem.id === undefined)) && this.editCreateMode) {
      this.showDiscardEditsDialog().show().done(
        (result: boolean) => {
          this.editCreateMode = !result;
          this.isDrawerOpen = !result;
        }
      )
    } else {
      this.editCreateMode = false;
      this.isDrawerOpen = false;
    }
  }

  getAlarmStatusCssClass(item: MetricInstanceAlert) {

    if (!item.enabled)
      return "disabled";

    switch (item.alarmStatus) {
      case MetricAlarmStatusEnum.ALARM_OFF:
        return "ok";
      case MetricAlarmStatusEnum.ALARM_ON:
      case MetricAlarmStatusEnum.ALARM_ON_RANGE_DOWN:
      case MetricAlarmStatusEnum.ALARM_ON_RANGE_UP:
      case MetricAlarmStatusEnum.ALARM_ON_STABLE:
      case MetricAlarmStatusEnum.ALARM_ON_CHANGED:
        return "warning";
      default:
        return "disabled"
    }
  }

  getTooltipTargetAndText(alert: HTMLDivElement, metric: MetricInstanceAlert) {
    this.tooltipTarget = alert;
    if (!!metric.metricInstance) {
      this.tooltipContent = `${metric.metricInstance.name}: ${this.appService.getParsedMetricValue(metric.metricInstance)} ${!!metric.metricInstance.dataUnit ? metric.metricInstance.dataUnit : ''}`;
      if (!!metric.metricInstance.deviceName) {
        this.tooltipContent = `${metric.metricInstance.deviceName} > ${this.tooltipContent}`;
      }
    }
    this.showTooltip = metric.enabled;
  }

  handleDetailSubscription() {
    if (this.subscribedDetailNotificationId !== undefined) {
      this.metricAlarmNotificationService.removeMetricIdsToAlarmDetailNotifications(this.subscribedDetailNotificationId);
      delete this.subscribedDetailNotificationId;
    }
    if (!!this.selectedItem && this.isDrawerOpen && !!this.selectedItem.metricInstance) {
      this.subscribedDetailNotificationId = this.selectedItem.metricInstance.id;
      this.metricAlarmNotificationService.addMetricIdsToAlarmDetailNotifications(this.subscribedDetailNotificationId);
    }
  }

}
